import React from "react";
import { Row, Col, Input, Button, Card, CardBody, CardHeader, FormGroup, Label, Table } from "reactstrap";
import Select from "react-select/creatable";
import { useAcquirerForm } from "../hooks/useAcquirerForm";
import { CARD_FLAGS } from "../constants";
import { CardHelper } from "../../../../utils/helpers";
import { OverlayLoader, LoadingButton } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";
import PropTypes from "prop-types";

/**
 * CustomOption
 * Componente visual para cada opção do react-select.
 */
const CustomOption = (props) => {
    const { innerProps, label, data } = props;
    return (
        <div {...innerProps} className="p-2 px-3 cursor-pointer select-option d-flex align-items-center gap-2">
            <i className={`${data?.icon || 'mdi mdi-plus-circle-outline'} text-muted`}></i>
            <span>{label}</span>
        </div>
    );
};

/**
 * CustomMenuList
 * Adiciona um rodapé no menu do Select com dica para criar nova bandeira.
 */
const CustomMenuList = (props) => {
    const { children, innerRef, innerProps } = props;
    return (
        <div ref={innerRef} {...innerProps}>
            {children}
            <div className="px-3 py-2 border-top bg-light d-flex align-items-center gap-2 text-muted" style={{ fontSize: '12px' }}>
                <i className="mdi mdi-lightbulb-on-outline text-warning"></i>
                <span>Digite o nome de uma bandeira personalizada e pressione Enter para criá-la.</span>
            </div>
        </div>
    );
};

/**
 * AcquirerEditor
 * Componente PURAMENTE VISUAL para edição de Adquirentes.
 * Toda a lógica de formulário está em useAcquirerForm.
 */
const AcquirerEditor = ({ acquirer, bankAccounts, onSave, onDelete, onCancel, isSaving, onChange }) => {
    const {
        formik,
        selectedFlagId,
        setSelectedFlagId,
        flagOptions,
        selectedOptions,
        actions
    } = useAcquirerForm(acquirer, onSave, onCancel, onChange);

    const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

    if (!acquirer) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-0 display-4">
                            <i className="mdi mdi-credit-card-plus"></i>
                        </div>
                    </div>
                    <h5>Selecione uma adquirente</h5>
                    <p className="text-muted" style={{ maxWidth: '300px' }}>
                        Escolha uma adquirente ao lado ou cadastre uma nova para configurar as taxas.
                    </p>
                </div>
            </div>
        );
    }

    const currentFlagRates = values.rates?.[selectedFlagId];
    const rateKeys = Object.keys(values.rates || {});

    return (
        <Card className="border-0 shadow-sm rounded-0 position-relative" style={{ height: 'calc(100vh - 150px)', overflow: 'hidden' }}>
            {/* ─── HEADER ─── */}
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary bg-gradient text-white rounded-0 shadow-sm">
                                <i className="mdi mdi-calculator-variant font-size-22"></i>
                            </span>
                        </div>
                        <div className="ms-3 overflow-hidden text-truncate" style={{ maxWidth: '250px' }}>
                            <h5 className="mb-0 fw-bold text-dark text-truncate">
                                {values.id ? values.name : "Nova Adquirente"}
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">Taxas e Parcelamentos</p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        {values.id && (
                            <Button
                                type="button"
                                color="light"
                                className="btn-md rounded-0 px-2 shadow-none text-danger border-0 d-flex align-items-center justify-content-center"
                                style={{ width: '38px', height: '38px' }}
                                onClick={() => onDelete(values.id, values.name)}
                                disabled={isSaving}
                                title="Excluir Adquirente"
                            >
                                <i className="mdi mdi-trash-can-outline font-size-18"></i>
                            </Button>
                        )}
                        {formik.dirty && (
                            <Button
                                type="button"
                                color="light"
                                className="btn-md rounded-0 px-3 shadow-none border-0"
                                onClick={actions.handleCancel}
                                disabled={isSaving}
                            >
                                Cancelar
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            type="button"
                            className="btn-md rounded-0 px-4 shadow-sm"
                            onClick={handleSubmit}
                            loading={isSaving}
                            loadingText="Salvando..."
                        >
                            Salvar
                        </LoadingButton>
                    </div>
                </div>
            </CardHeader>

            {/* ─── BODY ─── */}
            <CardBody className="p-4 overflow-auto">
                <OverlayLoader show={isSaving} label="Salvando..." />

                {/* ─── DADOS PRINCIPAIS ─── */}
                <Row className="mb-4">
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 d-flex align-items-center">
                            <i className="mdi mdi-information-outline me-2 font-size-15 text-primary"></i>
                            Dados Principais
                        </h6>
                    </Col>
                    <Col md={8}>
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Nome da Adquirente / Maquininha <span className="text-danger">*</span></Label>
                            <Input
                                name="name"
                                placeholder="Ex: Stone, Cielo..."
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={touched.name && !!errors.name}
                                className="form-control bg-light-subtle border-0"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase">Status</Label>
                            <Input
                                type="select"
                                name="status"
                                value={values.status || GLOBAL_STATUS.ACTIVE}
                                onChange={handleChange}
                                className="form-select bg-light-subtle border-0"
                            >
                                <option value={GLOBAL_STATUS.ACTIVE}>{GLOBAL_STATUS_LABELS[GLOBAL_STATUS.ACTIVE]}</option>
                                <option value={GLOBAL_STATUS.INACTIVE}>{GLOBAL_STATUS_LABELS[GLOBAL_STATUS.INACTIVE]}</option>
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>

                <hr className="my-4 opacity-50" />

                {/* ─── SELEÇÃO DE BANDEIRAS (MultiSelect) ─── */}
                <div className="mb-4">
                    <Label className="fw-medium text-muted small text-uppercase d-flex align-items-center mb-1">
                        <i className="mdi mdi-credit-card-plus-outline me-2 font-size-15 text-primary"></i>
                        Bandeiras Habilitadas
                    </Label>
                    <Select
                        isMulti
                        options={flagOptions}
                        value={selectedOptions}
                        onChange={actions.handleSelectChange}
                        placeholder="Busque ou digite o nome de uma bandeira..."
                        formatCreateLabel={(inputValue) => `➕ Criar bandeira: "${inputValue}"`}
                        noOptionsMessage={() => "Digite o nome da nova bandeira e pressione Enter"}
                        className="react-select-container shadow-sm"
                        classNamePrefix="react-select"
                        components={{ Option: CustomOption, MenuList: CustomMenuList }}
                        menuPortalTarget={document.body}
                        styles={{
                            control: (base) => ({
                                ...base,
                                backgroundColor: '#f8f9fa',
                                border: 'none',
                                padding: '5px'
                            }),
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                    />
                    <p className="text-muted font-size-11 mt-1 italic">* Selecione as bandeiras desejadas ou digite o nome de uma nova para configurar.</p>
                </div>

                {/* ─── ABAS DE BANDEIRAS + EDITOR DE TAXAS ─── */}
                {rateKeys.length > 0 && (
                    <>
                        <div className="mb-3">
                            <Label className="fw-medium text-muted small text-uppercase mb-2">Editar Taxas de:</Label>
                            <div className="d-flex flex-wrap gap-2">
                                {rateKeys.map(fId => (
                                    <Button
                                        key={fId}
                                        color={selectedFlagId === fId ? "primary" : "white"}
                                        size="sm"
                                        className={`rounded-0 px-3 shadow-sm border-0 d-flex align-items-center gap-2 ${selectedFlagId === fId ? 'text-white' : 'text-muted'}`}
                                        onClick={() => setSelectedFlagId(fId)}
                                    >
                                        <i className={CardHelper.getFlagIcon(fId, CARD_FLAGS)}></i>
                                        {CardHelper.getFlagName(fId, CARD_FLAGS)}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        {selectedFlagId && currentFlagRates && (
                            <div className="p-4 border rounded-0 bg-white shadow-sm border-primary border-opacity-10">
                                {/* Header da bandeira selecionada */}
                                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="avatar-xs me-2">
                                            <span className="avatar-title bg-primary-subtle text-primary rounded-0">
                                                <i className={CardHelper.getFlagIcon(selectedFlagId, CARD_FLAGS)}></i>
                                            </span>
                                        </div>
                                        <h6 className="mb-0 fw-bold">{CardHelper.getFlagName(selectedFlagId, CARD_FLAGS)}</h6>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            color="light"
                                            size="sm"
                                            type="button"
                                            className="border-0 font-size-11 text-uppercase fw-bold text-danger"
                                            onClick={() => actions.handleRemoveFlag(selectedFlagId)}
                                        >
                                            <i className="mdi mdi-trash-can-outline me-1"></i> Remover
                                        </Button>
                                        <Button
                                            color="light"
                                            size="sm"
                                            type="button"
                                            className="border-0 font-size-11 text-uppercase fw-bold"
                                            onClick={actions.handleLoadDefaults}
                                        >
                                            <i className="mdi mdi-refresh me-1"></i> Carregar Padrão
                                        </Button>
                                    </div>
                                </div>

                                {/* Conteúdo da bandeira: PIX vs Cartão */}
                                {selectedFlagId === 'pix' ? (
                                    <Row>
                                        <Col md={4}>
                                            <FormGroup className="mb-0">
                                                <Label className="small fw-medium text-muted text-uppercase">Taxa PIX (%)</Label>
                                                <div className="input-group input-group-lg">
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        name={`rates.${selectedFlagId}.rate`}
                                                        value={currentFlagRates.rate || 0}
                                                        onChange={handleChange}
                                                        className="bg-light-subtle border-0 fw-bold text-primary text-end"
                                                    />
                                                    <span className="input-group-text border-0 bg-light-subtle">%</span>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                ) : (
                                    <>
                                        {/* Taxa Débito */}
                                        <Row className="mb-4">
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label className="small fw-medium text-muted text-uppercase">Taxa Débito (%)</Label>
                                                    <div className="input-group">
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            name={`rates.${selectedFlagId}.debit`}
                                                            value={currentFlagRates.debit || 0}
                                                            onChange={handleChange}
                                                            className="bg-light-subtle border-0 fw-bold text-success text-end"
                                                        />
                                                        <span className="input-group-text border-0 bg-light-subtle font-size-11">%</span>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {/* Tabela de Crédito / Parcelamentos */}
                                        <div className="mb-3 d-flex justify-content-between align-items-center">
                                            <Label className="fw-medium text-muted small text-uppercase mb-0">Crédito e Parcelamentos</Label>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                outline
                                                className="rounded-0 px-3 py-1 border-dashed"
                                                type="button"
                                                onClick={actions.handleAddInstallment}
                                            >
                                                <i className="mdi mdi-plus-circle-outline me-1"></i> Nova Parcela
                                            </Button>
                                        </div>

                                        <div className="table-responsive rounded-0 border">
                                            <Table className="table-hover table-sm mb-0 align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th width="40%" className="ps-3 py-2 small text-uppercase text-muted">Parcelas</th>
                                                        <th className="text-end py-2 small text-uppercase text-muted">Taxa (%)</th>
                                                        <th width="60" className="text-center py-2"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(currentFlagRates.credit || []).map((item, index) => (
                                                        <tr key={index}>
                                                            <td className="ps-3">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Input
                                                                        type="number"
                                                                        name={`rates.${selectedFlagId}.credit.${index}.installments`}
                                                                        value={item.installments}
                                                                        onChange={handleChange}
                                                                        className="form-control-sm border-0 bg-transparent fw-bold text-center"
                                                                        style={{ width: '60px' }}
                                                                    />
                                                                    <span className="small text-muted">x</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Input
                                                                    type="number"
                                                                    step="0.01"
                                                                    name={`rates.${selectedFlagId}.credit.${index}.rate`}
                                                                    value={item.rate}
                                                                    onChange={handleChange}
                                                                    className="form-control-sm text-end border-0 bg-transparent fw-bold text-dark"
                                                                    placeholder="0.00"
                                                                />
                                                            </td>
                                                            <td className="text-center">
                                                                <Button
                                                                    color="link"
                                                                    size="sm"
                                                                    className="text-danger p-0 border-0"
                                                                    onClick={() => actions.handleRemoveInstallment(index)}
                                                                >
                                                                    <i className="mdi mdi-trash-can-outline font-size-16"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {(!currentFlagRates.credit || currentFlagRates.credit.length === 0) && (
                                                        <tr>
                                                            <td colSpan="3" className="text-center py-4 text-muted small italic">
                                                                Nenhuma taxa de crédito configurada.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Estado vazio de bandeiras */}
                {rateKeys.length === 0 && (
                    <div className="text-center py-5 border rounded-0 bg-light bg-opacity-50">
                        <i className="mdi mdi-credit-card-outline font-size-40 text-muted mb-2"></i>
                        <p className="text-muted mb-0">Habilite ao menos uma bandeira acima para configurar as taxas.</p>
                    </div>
                )}

                <hr className="my-4 opacity-50" />

                {/* ─── ANTECIPAÇÃO DE RECEBÍVEIS ─── */}
                <div className="mb-4">
                    <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 d-flex align-items-center">
                        <i className="mdi mdi-clock-fast me-2 font-size-15 text-warning"></i>
                        Antecipação de Recebíveis
                    </h6>

                    <Row>
                        <Col md={3}>
                            <FormGroup className="mb-3">
                                <Label className="fw-medium text-muted small text-uppercase">Antecipação</Label>
                                <div className="form-check form-switch pt-2">
                                    <Input
                                        type="switch"
                                        role="switch"
                                        checked={!!values.anticipation?.enabled}
                                        onChange={(e) => {
                                            formik.setFieldValue("anticipation.enabled", e.target.checked);
                                        }}
                                        className="form-check-input custom-switch-primary"
                                    />
                                    <Label className="ms-2 small text-muted">
                                        {values.anticipation?.enabled ? 'Ativada' : 'Desativada'}
                                    </Label>
                                </div>
                            </FormGroup>
                        </Col>

                        {values.anticipation?.enabled && (
                            <>
                                <Col md={3}>
                                    <FormGroup className="mb-3">
                                        <Label className="fw-medium text-muted small text-uppercase">Tipo</Label>
                                        <Input
                                            type="select"
                                            name="anticipation.type"
                                            value={values.anticipation?.type || "automatic"}
                                            onChange={handleChange}
                                            className="form-select bg-light-subtle border-0"
                                        >
                                            <option value="automatic">Automática (tudo antecipa)</option>
                                            <option value="manual">Manual (por venda)</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-3">
                                        <Label className="fw-medium text-muted small text-uppercase">Taxa / Mês (%)</Label>
                                        <div className="input-group">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                name="anticipation.feePerMonth"
                                                value={values.anticipation?.feePerMonth || 0}
                                                onChange={handleChange}
                                                className="bg-light-subtle border-0 fw-bold text-warning text-end"
                                            />
                                            <span className="input-group-text border-0 bg-light-subtle font-size-11">%</span>
                                        </div>
                                        <small className="text-muted">Taxa por mês antecipado</small>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup className="mb-3">
                                        <Label className="fw-medium text-muted small text-uppercase">Prazo de Depósito</Label>
                                        <div className="input-group">
                                            <span className="input-group-text border-0 bg-light-subtle font-size-11 fw-bold">D+</span>
                                            <Input
                                                type="number"
                                                min="0"
                                                max="31"
                                                name="anticipation.settlementDays"
                                                value={values.anticipation?.settlementDays || 1}
                                                onChange={handleChange}
                                                className="bg-light-subtle border-0 fw-bold text-primary text-end"
                                            />
                                        </div>
                                        <small className="text-muted">Dias para o dinheiro cair na conta</small>
                                    </FormGroup>
                                </Col>
                            </>
                        )}
                    </Row>
                </div>

                <hr className="my-4 opacity-50" />

                {/* ─── CONTA BANCÁRIA DE DEPÓSITO ─── */}
                <div className="mb-3">
                    <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 d-flex align-items-center">
                        <i className="mdi mdi-bank-outline me-2 font-size-15 text-info"></i>
                        Conta Bancária de Depósito
                    </h6>
                    <Row>
                        <Col md={6}>
                            <FormGroup className="mb-0">
                                <Label className="fw-medium text-muted small text-uppercase">Conta de Destino</Label>
                                <Input
                                    type="select"
                                    name="idBankAccount"
                                    value={values.idBankAccount || ""}
                                    onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const selectedAccount = (bankAccounts || []).find(a => a.id === selectedId);
                                        formik.setFieldValue("idBankAccount", selectedId);
                                        formik.setFieldValue("bankAccountName", selectedAccount ? selectedAccount.description : "");
                                    }}
                                    className="form-select bg-light-subtle border-0"
                                >
                                    <option value="">Selecione uma conta bancária...</option>
                                    {(bankAccounts || []).map(account => (
                                        <option key={account.id} value={account.id}>{account.description}</option>
                                    ))}
                                </Input>
                                <small className="text-muted">A adquirente deposita os valores nesta conta</small>
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            </CardBody>
        </Card>
    );
};

AcquirerEditor.propTypes = {
    acquirer: PropTypes.object,
    bankAccounts: PropTypes.array,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default AcquirerEditor;
